import { useCallback, useEffect, useState } from 'react';
import recordTypeService from '@/services/recordType/recordTypeService';

// Simple module-level cache so switching between types (or re-opening a matter)
// doesn't re-fetch a layout we already have.
const layoutCache = new Map();

/**
 * Fetch and cache the sections/fields layout for a record type.
 * Returns { layout, loading, error, refetch }.
 */
export const useRecordTypeLayout = (recordTypeId) => {
  const [layout, setLayout] = useState(() => layoutCache.get(recordTypeId) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLayout = useCallback(
    async (id, { force = false } = {}) => {
      if (!id) {
        setLayout(null);
        return null;
      }

      if (!force && layoutCache.has(id)) {
        const cached = layoutCache.get(id);
        setLayout(cached);
        return cached;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, status } = await recordTypeService.getRecordTypeLayout(id);
        if (status) {
          layoutCache.set(id, data);
          setLayout(data);
          return data;
        }
        return null;
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLayout(recordTypeId);
  }, [recordTypeId, fetchLayout]);

  const refetch = useCallback(
    () => fetchLayout(recordTypeId, { force: true }),
    [recordTypeId, fetchLayout]
  );

  return { layout, loading, error, refetch };
};

export const invalidateRecordTypeLayout = (recordTypeId) => {
  if (recordTypeId) {
    layoutCache.delete(recordTypeId);
  } else {
    layoutCache.clear();
  }
};
